/**
 * Vercel Runtime Logger - Optimized for Vercel's logging system
 * 
 * This logger directly uses console methods which Vercel automatically captures
 * in the Logs tab of the dashboard. No custom endpoints needed.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogEnvironment = 'development' | 'preview' | 'production';

interface LogContext {
  timestamp: string;
  level: LogLevel;
  component: string;
  action: string;
  sessionId: string;
  environment: LogEnvironment;
  requestId?: string;
  correlationId?: string;
  release?: string;
  route?: string;
  userId?: string;
  data?: any;
  error?: any;
  duration?: number;
  url?: string;
  method?: string;
}

interface LoggerConfig {
  enableDebug: boolean;
  enableConsoleInProduction: boolean;
  maxLogSize: number;
  sessionTracking: boolean;
}

class VercelRuntimeLogger {
  private sessionId: string;
  private environment: LogEnvironment;
  private config: LoggerConfig;
  private requestCounter: number = 0;
  private isVercel: boolean;
  private release: string;
  private logBuffer: LogContext[] = [];
  private maxBufferSize: number = 100;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.environment = this.detectEnvironment();
    this.isVercel = this.detectVercel();
    this.release = this.detectRelease();
    
    // Configure based on environment
    this.config = {
      enableDebug: this.environment === 'development' || process.env.REACT_APP_DEBUG === 'true',
      enableConsoleInProduction: true, // Always true for Vercel to capture logs
      maxLogSize: 256 * 1024, // Vercel's limit: 256KB per log line
      sessionTracking: true,
    };

    this.initializeLogger();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private detectEnvironment(): LogEnvironment {
    if (typeof window === 'undefined') return 'development';
    
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
    if (hostname.includes('-') && hostname.includes('.vercel.app')) {
      return 'preview';
    }
    return 'production';
  }

  private detectVercel(): boolean {
    // Check if we're running in a Vercel environment
    return !!(
      process.env.REACT_APP_VERCEL ||
      process.env.VERCEL ||
      process.env.VERCEL_ENV ||
      (typeof window !== 'undefined' && window.location.hostname.includes('vercel.app'))
    );
  }

  private detectRelease(): string {
    return (
      process.env.REACT_APP_RELEASE_VERSION ||
      process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA ||
      process.env.VERCEL_GIT_COMMIT_SHA ||
      "local"
    );
  }

  private initializeLogger() {
    // Log initialization for debugging
    if (this.isVercel) {
      console.log('[Vercel Logger Initialized]', {
        sessionId: this.sessionId,
        environment: this.environment,
        isVercel: this.isVercel,
        timestamp: new Date().toISOString(),
      });
    }

    // Attach global error handlers
    this.attachErrorHandlers();
  }

  private attachErrorHandlers() {
    if (typeof window === 'undefined') return;

    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.error('Global', 'UNHANDLED_ERROR', event.error || event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Global', 'UNHANDLED_REJECTION', event.reason, {
        type: 'promise_rejection',
      });
    });
  }

  private formatLog(
    level: LogLevel,
    component: string,
    action: string,
    data?: any,
    error?: any
  ): LogContext {
    const log: LogContext = {
      timestamp: new Date().toISOString(),
      level,
      component,
      action,
      sessionId: this.sessionId,
      environment: this.environment,
      release: this.release,
    };

    // Add context from window if available
    if (typeof window !== 'undefined') {
      log.url = window.location.href;
      log.route = window.location.pathname;
    }

    // Add request ID for tracking
    if (this.config.sessionTracking) {
      log.requestId = `${this.sessionId}_${++this.requestCounter}`;
    }

    // Sanitize and add data
    if (data !== undefined) {
      log.data = this.sanitizeData(data);
      if (data && typeof data === 'object' && 'correlationId' in data) {
        log.correlationId = String((data as Record<string, unknown>).correlationId);
      }
    }

    // Format error properly
    if (error) {
      log.error = {
        message: error?.message || String(error),
        stack: error?.stack,
        name: error?.name,
        code: error?.code,
      };
    }

    return log;
  }

  private sanitizeData(data: any): any {
    // Remove sensitive information
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }

    if (typeof data !== 'object' || data === null) return data;

    const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization', 'cookie'];
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    if (Array.isArray(sanitized)) {
      return sanitized.map(item => this.sanitizeData(item));
    }
    
    for (const key of Object.keys(sanitized)) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      } else if (typeof sanitized[key] === 'string') {
        sanitized[key] = this.sanitizeString(sanitized[key]);
      }
    }

    return sanitized;
  }

  private sanitizeString(value: string): string {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      try {
        const url = new URL(value);
        const sensitiveParams = ['token', 'signature', 'sig', 'apikey', 'api_key', 'auth', 'key'];
        sensitiveParams.forEach((param) => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[REDACTED]');
          }
        });
        return url.toString();
      } catch {
        return value;
      }
    }

    return value;
  }

  private truncateLog(log: any): any {
    // Ensure log doesn't exceed Vercel's 256KB limit
    const logString = JSON.stringify(log);
    if (logString.length > this.config.maxLogSize) {
      return {
        ...log,
        data: '[TRUNCATED - Exceeded 256KB limit]',
        originalSize: logString.length,
      };
    }
    return log;
  }

  private outputToConsole(log: LogContext) {
    // Store in buffer for debugging
    this.logBuffer.push(log);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }

    // Truncate if needed
    const logToOutput = this.truncateLog(log);
    
    // Format for better readability in Vercel logs
    const prefix = `[${logToOutput.component}:${logToOutput.action}]`;
    const metadata = {
      sessionId: logToOutput.sessionId,
      requestId: logToOutput.requestId,
      correlationId: logToOutput.correlationId,
      environment: logToOutput.environment,
      release: logToOutput.release,
      timestamp: logToOutput.timestamp,
    };

    // Use appropriate console method based on level
    switch (logToOutput.level) {
      case 'error':
        console.error(prefix, metadata, logToOutput.error || '', logToOutput.data || '');
        break;
      case 'warn':
        console.warn(prefix, metadata, logToOutput.data || '');
        break;
      case 'info':
        console.log(prefix, metadata, logToOutput.data || '');
        break;
      case 'debug':
        if (this.config.enableDebug) {
          console.log(`[DEBUG] ${prefix}`, metadata, logToOutput.data || '');
        }
        break;
    }
  }

  // Public logging methods
  debug(component: string, action: string, data?: any) {
    if (!this.config.enableDebug) return;
    
    const log = this.formatLog('debug', component, action, data);
    this.outputToConsole(log);
  }

  info(component: string, action: string, data?: any) {
    const log = this.formatLog('info', component, action, data);
    this.outputToConsole(log);
  }

  warn(component: string, action: string, data?: any) {
    const log = this.formatLog('warn', component, action, data);
    this.outputToConsole(log);
  }

  error(component: string, action: string, error: any, data?: any) {
    const log = this.formatLog('error', component, action, data, error);
    this.outputToConsole(log);
  }

  createCorrelationId(prefix: string = 'corr'): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  // Track API calls with timing
  async trackApiCall(
    component: string,
    url: string,
    options: RequestInit,
    handler: () => Promise<Response>
  ): Promise<Response> {
    const startTime = performance.now();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    this.info(component, 'API_REQUEST', {
      url,
      method: options.method || 'GET',
      requestId,
      headers: this.sanitizeData(options.headers),
    });

    try {
      const response = await handler();
      const duration = Math.round(performance.now() - startTime);

      this.info(component, 'API_RESPONSE', {
        url,
        requestId,
        status: response.status,
        statusText: response.statusText,
        duration: `${duration}ms`,
        ok: response.ok,
      });

      // Warn about slow requests
      if (duration > 3000) {
        this.warn(component, 'SLOW_API', {
          url,
          duration: `${duration}ms`,
          threshold: '3000ms',
        });
      }

      return response;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);

      this.error(component, 'API_ERROR', error, {
        url,
        requestId,
        duration: `${duration}ms`,
        method: options.method || 'GET',
      });

      throw error;
    }
  }

  // Get session info for debugging
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      environment: this.environment,
      isVercel: this.isVercel,
      release: this.release,
      logsInBuffer: this.logBuffer.length,
      requestCount: this.requestCounter,
    };
  }

  // Get buffered logs for debugging
  getBufferedLogs(): LogContext[] {
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearBuffer() {
    this.logBuffer = [];
    console.log('[Logger Buffer Cleared]', this.getSessionInfo());
  }

  // Export logs for support
  exportLogs(): string {
    const exportData = {
      sessionInfo: this.getSessionInfo(),
      logs: this.logBuffer,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      url: typeof window !== 'undefined' ? window.location.href : 'N/A',
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  // Diagnostic method to test logging
  testLogging() {
    console.log('=== Vercel Logger Diagnostic Test ===');
    console.log('Session Info:', this.getSessionInfo());
    
    this.info('Test', 'INFO_TEST', { message: 'Testing info log' });
    this.warn('Test', 'WARN_TEST', { message: 'Testing warning log' });
    this.error('Test', 'ERROR_TEST', new Error('Test error'), { context: 'Testing error log' });
    
    console.log('=== Test Complete - Check Vercel Logs Tab ===');
  }
}

// Create singleton instance
const vercelRuntimeLogger = new VercelRuntimeLogger();

// Expose to window for debugging in production
if (typeof window !== 'undefined') {
  (window as any).__vercelLogger = vercelRuntimeLogger;
  
  // Add keyboard shortcut for diagnostics (Ctrl+Shift+L)
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'L') {
      console.log('=== Logger Diagnostic Info ===');
      console.log(vercelRuntimeLogger.getSessionInfo());
      console.log('Recent Logs:', vercelRuntimeLogger.getBufferedLogs().slice(-5));
    }
  });
}

export default vercelRuntimeLogger;
